import type { Offer } from "@/hooks/use-offers";

export const DEFAULT_BUDGET_INTRO =
  "Budsjettet beskriver vedlagt løsningsforslag fra Sølvrev. Prisen tar forbehold om at filmene ferdigstilles i offline + online med kunde. Evt revisjoner utover dette faktureres løpende.";

export const DEFAULT_TRAVEL_NOTE =
  "Eventuelle reisekostnader er ikke inkludert i budsjett.";

export const EXPORT_MVA_LINE = "Alle priser er eks. mva.";

type InvoiceOffer = Pick<
  Offer,
  | "invoice_terms"
  | "startup_invoice_pct"
  | "final_invoice_pct"
  | "bills_travel_per_medgatt"
  | "show_invoice_terms"
>;

function invoiceTerms(
  offer: Pick<
    Offer,
    "invoice_terms" | "startup_invoice_pct" | "final_invoice_pct"
  >,
): { label: string; pct: number }[] {
  if (Array.isArray(offer.invoice_terms) && offer.invoice_terms.length > 0) {
    return offer.invoice_terms;
  }
  return [
    { label: "Oppstart", pct: offer.startup_invoice_pct || 60 },
    { label: "Levering", pct: offer.final_invoice_pct || 40 },
  ];
}

/**
 * VIB-993: Kort intern form ("60% oppstart, 40% levering"). Beholdt for
 * bakoverkompatibilitet; kunde-eksporten bruker {@link buildInvoiceTermsSentence}.
 */
export function buildInvoiceTermsText(
  offer: Pick<
    Offer,
    | "invoice_terms"
    | "startup_invoice_pct"
    | "final_invoice_pct"
    | "bills_travel_per_medgatt"
  >,
): string {
  const terms = invoiceTerms(offer);
  return [
    terms.map((t) => `${t.pct}% ${t.label.toLowerCase()}`).join(", "),
    offer.bills_travel_per_medgatt ? "reise faktureres separat (medgått)" : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

const INVOICE_PHRASES: Record<string, string> = {
  oppstart: "ved godkjent tilbud",
  levering: "ved leveranse",
};

function phraseForTermLabel(label: string): string {
  const key = label.trim().toLowerCase();
  return INVOICE_PHRASES[key] ?? `ved ${key}`;
}

function joinNoAnd(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  if (parts.length === 2) return `${parts[0]}, og ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, og ${parts[parts.length - 1]}`;
}

/**
 * Kunde-setning som i Christers tilbudsmal:
 * "Faktureres 60% ved godkjent tilbud, og 40% ved leveranse."
 */
export function buildInvoiceTermsSentence(
  offer: Pick<
    Offer,
    | "invoice_terms"
    | "startup_invoice_pct"
    | "final_invoice_pct"
    | "bills_travel_per_medgatt"
  >,
): string {
  const parts = invoiceTerms(offer).map(
    (t) => `${t.pct}% ${phraseForTermLabel(t.label)}`,
  );
  let sentence = `Faktureres ${joinNoAnd(parts)}.`;
  if (offer.bills_travel_per_medgatt) {
    sentence += " Reise faktureres separat (medgått).";
  }
  return sentence;
}

export type LeftPanelBlock = {
  key: "body" | "invoice" | "travel" | "mva";
  text: string;
  bold?: boolean;
};

/**
 * Venstrepanel-blokker i mal-rekkefølge. Tom custom_terms_text → standard
 * intro + reisenotat. Egen tekst erstatter intro/reise, men fakturasetning
 * og MVA kommer alltid fra invoice_terms (ikke bakt inn i custom-feltet).
 */
export function buildLeftPanelBlocks(
  offer: InvoiceOffer,
  customTermsText: string | null | undefined,
): LeftPanelBlock[] {
  const custom = (customTermsText ?? "").trim();
  const blocks: LeftPanelBlock[] = [];
  if (custom) {
    blocks.push({ key: "body", text: custom });
  } else {
    blocks.push({ key: "body", text: DEFAULT_BUDGET_INTRO });
  }
  if (offer.show_invoice_terms !== false) {
    blocks.push({ key: "invoice", text: buildInvoiceTermsSentence(offer) });
  }
  if (!custom) {
    blocks.push({ key: "travel", text: DEFAULT_TRAVEL_NOTE });
  }
  blocks.push({ key: "mva", text: EXPORT_MVA_LINE, bold: true });
  return blocks;
}

/** Én fotnote-blokk (tester + eventuell enkel fallback). */
export function buildLeftFooterText(offer: InvoiceOffer): string {
  return buildLeftPanelBlocks(offer, null)
    .filter((b) => b.key !== "body")
    .map((b) => b.text)
    .join("\n\n");
}
